import React, { useMemo } from 'react';

import useFormatDateTime from '@/hooks/format/useFormatDateTime';
// Hooks
import useSocialProfiles from '@/hooks/swr/useSocialProfiles';
import useSPGroups from '@/utils/hooks/swr/group/useSPGroups';
import useTranslation from '@/utils/hooks/translation/useTranslation';

import PropTypes from 'prop-types';

// Libs
import { DASHBOARD_VIEW } from '@/lib/routes/privateRoutes';

// Components
import Divider from '@/components/base/Divider/Divider';
import Icon from '@/components/base/Icon';
import SocialProfilesAndGroupsListPopover from '@/components/composite/SocialProfiles/SocialProfilesAndGroupsListPopover';
import DashboardActionsModal from '@/components/pages/custom-dashboards/modals/DashboardActionsModal/DashboardActionsModal';

const DashboardCard = ({ dashboard, mutateDashboards, user, index }) => {
    const { t } = useTranslation('dashboard');
    const { formatDateTime } = useFormatDateTime();
    const { activatedSocialProfiles } = useSocialProfiles({
        revalidateOnMount: false,
        shouldRetryOnError: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });
    const { spGroups } = useSPGroups({
        revalidateOnMount: false,
        shouldRetryOnError: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });

    const { list, hasNonActiveSp } = useMemo(() => {
        const finalList = [];
        let finalHasNonActiveSp = false;

        dashboard?.data?.groups?.forEach((groupId) => {
            const group = spGroups.find((group) => group.id === groupId);

            if (group?.active) {
                finalList.push(group);
            } else {
                finalHasNonActiveSp = true;
            }
        });

        dashboard?.data?.social_profiles?.forEach((socialProfileId) => {
            const socialProfile = activatedSocialProfiles.find((sp) => sp.id === socialProfileId);

            if (!socialProfile) {
                finalHasNonActiveSp = true;
            } else {
                finalList.push(socialProfile);
            }
        });

        return { list: finalList, hasNonActiveSp: finalHasNonActiveSp };
    }, [activatedSocialProfiles, dashboard.data.groups, dashboard.data.social_profiles, spGroups]);

    return (
        <a
            id={`dashboard-card-${index}`}
            href={DASHBOARD_VIEW.replace('[id]', dashboard.id)}
            className="group relative flex h-[18.125rem] w-full flex-1 flex-col items-center rounded-md bg-white transition-shadow hover:shadow-lg dark:bg-icoDarkMode-anthracite">
            <DashboardActionsModal dashboard={dashboard} mutateDashboards={mutateDashboards} />
            <div className="flex gap-1 pb-8 pt-20">
                {list.length > 0 ? (
                    <SocialProfilesAndGroupsListPopover
                        btnClassNames="bg-icoGray-100 dark:bg-icoDarkMode-deepBlack w-10.5 h-10.5"
                        className="space-x-2.5"
                        avatarSize="base"
                        maxDisplayed={3}
                        profiles={list?.filter(
                            (profile) => profile?.active || profile?.active === null
                        )}
                    />
                ) : null}
            </div>
            <div className="w-full flex-1 px-4 text-center">
                <p className="line-clamp-2 text-xl" title={dashboard.name}>
                    {dashboard.name}
                </p>
                {hasNonActiveSp ? (
                    <div className="mt-2 flex items-center justify-center space-x-1.5 text-xs text-icoRed">
                        <Icon icon="disconnect" />
                        <p className="font-GMedium">{t('contains_deactivated_profiles')}</p>
                    </div>
                ) : null}
            </div>
            <div className="h-[3.375rem] w-full">
                <Divider className="mb-3 px-8" />
                <div className="px-4 text-center">
                    <p className="font-GMedium text-xs text-icoGray-400" data-testid="last-updated">
                        {t('last_updated')} :{' '}
                        {formatDateTime(
                            dashboard.updated_at,
                            `${user.settings?.date_format}_split`
                        )}
                    </p>
                </div>
            </div>
        </a>
    );
};

DashboardCard.propTypes = {
    dashboard: PropTypes.object,
    mutateDashboards: PropTypes.func,
    user: PropTypes.object
};

export default DashboardCard;
